import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

import Logo from "@/components/common/Logo";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import GridBackground from "@/components/common/GridBackground";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiError } from "@/lib/api";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const { t } = useLanguage();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({ ...form, email: form.email.trim().toLowerCase() });
      toast.success("Account created.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      <div className="hidden lg:flex relative border-r border-border overflow-hidden">
        <GridBackground />
        <div className="relative z-10 w-full flex flex-col justify-between p-10">
          <Link to="/"><Logo size={32} /></Link>
          <div className="max-w-md">
            <div className="text-overline mb-5">NEW · CLIENT · CHANNEL</div>
            <h2 className="text-4xl font-semibold tracking-tight text-white leading-tight">
              Open a client channel in minutes.
            </h2>
            <p className="mt-6 text-sm text-secondary-foreground/70 leading-relaxed">
              Your account is scoped by default to Standard access (project visibility and ticketing). Upgrade to Premium to unlock the Infrastructure Calculator and Webhook APIs.
            </p>
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">
            <span>TIER · STANDARD BY DEFAULT</span>
            <span>RBAC · ENFORCED</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="px-6 lg:px-10 h-16 flex items-center justify-between border-b border-border">
          <Link to="/" className="flex items-center gap-2 text-xs font-mono tracking-widest text-muted-foreground hover:text-primary transition-colors" data-testid="register-back-home">
            <ArrowLeft className="w-3 h-3" /> HOME
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 lg:px-10 py-10">
          <div className="w-full max-w-md">
            <div className="text-overline mb-3">CLIENT ONBOARDING</div>
            <h1 className="text-3xl font-semibold text-white tracking-tight" data-testid="register-title">{t("auth.register.title")}</h1>
            <p className="mt-3 text-sm text-secondary-foreground/70">{t("auth.register.subtitle")}</p>
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
              <Row>
                <Field label={t("auth.name")}>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" className={inp} />
                </Field>
                <Field label={t("auth.company")}>
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="register-company-input" className={inp} />
                </Field>
              </Row>
              <Field label={t("auth.email")}>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" className={inp} />
              </Field>
              <Field label={t("auth.password")}>
                <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input" className={inp} />
              </Field>
              <button type="submit" disabled={busy} data-testid="register-submit-button" className="w-full h-11 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors disabled:opacity-50 glow-blue inline-flex items-center justify-center gap-2">
                {busy ? "…" : t("auth.register.submit")} {!busy && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
            <div className="mt-8 text-sm text-secondary-foreground/70">
              {t("auth.haveaccount")}{" "}
              <Link to="/login" className="text-primary link-underline" data-testid="register-login-link">{t("auth.signin")}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full h-11 px-4 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm tracking-wide text-white";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 text-[10px] font-mono tracking-[0.24em] text-muted-foreground uppercase">{label}</div>
      {children}
    </label>
  );
}
function Row({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}
